import * as React from 'react';
import {useSelected, useSlate, useFocused, ReactEditor} from 'slate-react';
import {Transforms} from 'slate';

import {
  ExpressionBinding,
  getBindingLabel,
} from '@packages/systems/ExpressionEditor-utils';
import {styledDiv, emotionStyled, tokens} from '@packages/utilities/styled';
import {chainFunctions} from '@packages/utilities/component';
import {
  parseExpression,
  stringifyExpression,
  Expressions,
  matchExpression,
  Variable,
  hasErrors,
  hasHoles,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {type Expression, EHole} from '@packages/systems/wfdl-ast';
import {constant, emptyArray} from '@packages/utilities/fp/utils';
import {lighten, darken} from '@packages/systems/core/utils/ColorUtils';
import Hitarea from '@packages/systems/design-system/Hitarea';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';

import {
  ArgumentSettings,
  HasHolesIcon,
} from '@packages/systems/ExpressionEditor-utils-ui';
import {
  FlowStatusWarning12 as Warning,
  SettingsXsmall as SettingsIcon,
} from '@packages/systems/ui/icons';
import OnelineLabel from './OnelineLabel';
import {getShortTokenLabel, parseJson, listOfTokens} from './utils';
import {useAddToken} from './useAddToken';
import {
  type SlateState as SlateEditorNodes,
  type SlateNode,
  BINDED_PILL,
} from './slateTextUI';

const {deconstructCall} = ContextualExpressions;

/** Matching function that attempts to get the name for the provided expression
 * depending on the type of that expression
 */
const pillLabelHandler = matchExpression({
  Variable: Variable.getName,
  Function: constant('Unknown Function'),
  Select: (val) => val.prop,
  _: constant('Unknown Binding'),
});

const Settings = styledDiv(
  ({theme, hover}) => {
    const radius = theme.control.borderRadiusPill;
    const bg = theme.colors.logicFlows.backgroundVivid;

    return {
      display: 'flex',
      position: 'relative',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: hover ? lighten(5)(bg) : bg,
      height: theme.lineHeights.copy,
      paddingRight: theme.gutters.x4,
      paddingLeft: theme.gutters.x4,
      color: theme.Button.dynamic.color,
      borderRadius: `0 ${radius}px ${radius}px 0`,
      cursor: 'default',
      boxShadow: `1px 0 0 ${darken(5)(bg)} inset`,
      border: `1px solid transparent`,
    };
  },
  {
    selected: ({theme, malformed}) => ({
      backgroundColor: theme.colors.white,
      color: malformed ? theme.colors.danger : theme.colors.accent,
    }),
    showOutline: ({theme}) => ({
      border: `1px solid ${theme.colors.accent}`,
    }),
  }
);

const Pill = styledDiv(
  ({needsConfig, theme}) => {
    const radius = theme.control.borderRadiusPill;
    return {
      backgroundColor: theme.colors.logicFlows.backgroundVivid,
      height: theme.lineHeights.copy,
      fontSize: '1em',

      paddingRight: theme.gutters.x8,
      paddingLeft: needsConfig ? theme.gutters.x4 : theme.gutters.x8,
      color: theme.Button.dynamic.color,
      borderRadius: radius,

      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minWidth: 0,
      columnGap: theme.gutters.x4,
      fontFamily: theme.typography.copy.fontFamily,

      userSelect: 'none',
      border: `1px solid transparent`,
    };
  },
  {
    malformed: ({theme}) => ({
      backgroundColor: theme.colors.dangerBackground,
      color: theme.Button.danger.color,
    }),
    missing: ({theme}) => ({
      backgroundColor: theme.colors.backgroundVividest,
    }),
    hover: ({style}) => ({
      backgroundColor: lighten(5)(style.backgroundColor),
      cursor: 'default',
    }),
    selected: ({theme, malformed}) => ({
      backgroundColor: theme.colors.white,
      color: malformed ? theme.colors.danger : theme.colors.accent,
    }),
    showOutline: ({theme}) => ({
      border: `1px solid ${theme.colors.accent}`,
    }),
  }
);

const StyledSelectedBackground = emotionStyled('div')(
  {display: 'flex', minWidth: 0},
  {
    variants: {
      selected: {
        true: {
          backgroundColor: tokens.colors.blueBorder,
        },
      },

      settingsPosition: {
        true: {
          position: 'absolute',
          right: 0,
        },
      },

      isSettingsOpen: {
        true: {
          visibility: 'hidden',
        },
      },
    },
  }
);

const PillWrapper = emotionStyled('div')({
  display: 'inline-flex',
  position: 'relative',
});

const StyledWarning = styledDiv(({theme}) => ({
  color: theme.colors.warning,
  backgroundColor: theme.colors.logicFlows.statusIconBackground,
}));

const actions = {
  HOVERED: 'HOVERED' as const,
  NOT_HOVERED: 'NOT_HOVERED' as const,
  OPENED: 'OPENED' as const,
  CLOSED: 'CLOSED' as const,
} as const;

const initialState = {
  opened: false,
  hovered: false,
} as const;

const reducer = (
  state = initialState,
  action: 'CLOSED' | 'HOVERED' | 'NOT_HOVERED' | 'OPENED'
) => {
  if (action === actions.HOVERED) return {...state, hovered: true};
  if (action === actions.NOT_HOVERED) return {...state, hovered: false};
  if (action === actions.OPENED) return {hovered: false, opened: true};
  if (action === actions.CLOSED) return {hovered: false, opened: false};
  return state;
};

function useCallSettingsState() {
  // @ts-expect-error - TS2769 - No overload matches this call.
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const handlers = React.useMemo(
    () => ({
      onHover() {
        // @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
        dispatch(actions.HOVERED);
      },
      onLeave() {
        // @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
        dispatch(actions.NOT_HOVERED);
      },
      onOpen() {
        // @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
        dispatch(actions.OPENED);
      },
      onClose() {
        // @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
        dispatch(actions.CLOSED);
      },
    }),
    []
  );
  return [state, handlers];
}

export const BindingPill = React.memo<{
  expr: ContextualExpression;
  token: string;
  onChange: () => void;
  attributes: Record<any, any>;
  children: React.ReactNode;
  element: SlateNode;
}>(function BindingPill({
  expr,
  token,
  attributes,
  onChange,
  children,
  element,
}) {
  const selected = useSelected();
  const focused = useFocused();
  const editor = useSlate();
  const addToken = useAddToken(onChange);

  const [settingsOpenState, handlers] = useCallSettingsState();

  const [textSelection, setTextSelection] = React.useState(
    // @ts-expect-error - TS2531 - Object is possibly 'null'.
    window.getSelection().toString().length
  );

  React.useEffect(() => {
    // @ts-expect-error - TS2531 - Object is possibly 'null'.
    setTextSelection(window.getSelection().toString().length);
  }, [selected]);

  // We check the selection to ensure text has been selected
  // This prevents showing the pill as selected during a click of the pill
  const showAsSelected = selected && focused && textSelection;
  const showOutline = selected && textSelection === 0;

  // Get the path to this specific binded pill
  // and use that to get the full annotated expression for that pill
  const pillSlatePath = ReactEditor.findPath(editor as ReactEditor, element);
  const {tokenExpression} = getFullTokenExpression(
    // @ts-expect-error - TS2345 - Argument of type 'Descendant[]' is not assignable to parameter of type 'SlateState'.
    editor.children,
    pillSlatePath,
    expr
  );

  const json = parseJson(token);
  const exprParseResult = json ? parseExpression(json) : null;
  const rawTokenExpr =
    exprParseResult && exprParseResult.type === 'WellformedExpression'
      ? exprParseResult.val
      : null;

  if (rawTokenExpr == null) {
    return (
      <StyledSelectedBackground
        data-automation-id="binding-pill_malformed"
        selected={Boolean(showAsSelected)}
      >
        <Hitarea>
          <Pill malformed selected={showAsSelected}>
            <OnelineLabel>Malformed Token</OnelineLabel>
          </Pill>
        </Hitarea>
      </StyledSelectedBackground>
    );
  }

  // This only occurs during the instance where this binding pill needs to be rendered
  // but the overall expression does not yet contain the expression for this specific binding
  // One instance of this is when a binding pill(s) is copy-pasted from one input field to another
  if (!tokenExpression) {
    return null;
  }

  const tokenExprAnn = getAnn(tokenExpression);

  const tokenLabel = Expressions.isCall(rawTokenExpr)
    ? // Render binding name of function calls

      getBindingLabel(tokenExprAnn.env.context.values, rawTokenExpr)
    : // A binding can be bound to a Holey value, in which case we want to
      // show the label of the previous
      pillLabelHandler(rawTokenExpr);

  const tokenShortLabel = getShortTokenLabel(tokenLabel);

  const onBindingChange = (binding?: Expression | null) => {
    const tokenToAdd = binding ? stringifyExpression(binding) : null;
    // @ts-expect-error - TS2322 - Type 'BaseSelection' is not assignable to type 'Location | undefined'.
    Transforms.delete(editor, {at: editor.selection, unit: 'word'});
    if (tokenToAdd) {
      addToken(tokenToAdd);
    }
  };

  // The annotation for the token expression can be null when an output variable
  // binded to the token expression is no longer accessible by this expression.
  // This can occur in these scenarios
  // - a Logic Block is reordered to a position where an output variable is no longer available
  // - Text that contains binding pills is copy-pasted from one block, to a block where
  //   the variable binded to the pill is missing due to not being within the scope of that block
  if (hasErrors(tokenExpression)) {
    return (
      <PillWrapper {...attributes} contentEditable={false}>
        <ExpressionBinding
          expression={expr}
          onChange={onBindingChange}
          button={(openPopover, targetRef) => (
            <span ref={targetRef}>
              <StyledSelectedBackground
                data-automation-id={`binding-pill_missing_binding_${tokenLabel}`}
                selected={Boolean(showAsSelected)}
              >
                <Hitarea>
                  <Pill missing onClick={openPopover} selected={showAsSelected}>
                    <TooltipTrigger
                      placement="top"
                      alternate
                      content={
                        tokenExprAnn.type
                          ? 'This binding is invalid and should be replaced'
                          : 'There is no source for this binding'
                      }
                    >
                      <StyledWarning>
                        <Warning />
                      </StyledWarning>
                    </TooltipTrigger>
                    <s>
                      <OnelineLabel>{tokenLabel}</OnelineLabel>
                    </s>
                  </Pill>
                </Hitarea>
              </StyledSelectedBackground>
            </span>
          )}
          bindUIVariant="custom"
        />
        {children}
      </PillWrapper>
    );
  }

  const call = tokenExpression ? deconstructCall(tokenExpression) : null;
  const args = call ? call.args : emptyArray;
  const fun = call ? call.fun : false;
  const hasArgs = args.length > 0;

  // RENDER NON-FUNCTION PILLS
  // ------------------------------------------------
  if (!fun || !hasArgs) {
    return (
      <PillWrapper
        {...attributes}
        onMouseOver={handlers.onHover}
        onMouseLeave={handlers.onLeave}
        contentEditable={false}
      >
        <ExpressionBinding
          expression={tokenExpression}
          onChange={onBindingChange}
          button={(openPopover, targetRef) => (
            <span ref={targetRef}>
              <StyledSelectedBackground
                data-automation-id={`binding-pill_${tokenLabel}`}
                selected={Boolean(showAsSelected)}
              >
                <Hitarea>
                  <Pill
                    onClick={openPopover}
                    selected={showAsSelected}
                    showOutline={selected}
                  >
                    <OnelineLabel>{tokenLabel}</OnelineLabel>
                  </Pill>
                </Hitarea>
              </StyledSelectedBackground>
            </span>
          )}
          bindUIVariant="custom"
        />
        {children}
      </PillWrapper>
    );
  }

  const argumentsDispatch = (action: any) => {
    const newExpr =
      action.type === 'EXPRESSION_ACTION'
        ? action.payload.newExpression
        : action.type === 'EXPRESSION_BIND_CHANGED'
          ? action.binding || EHole()
          : null;

    if (!newExpr) return;

    const newTokenExpr = Expressions.setIn(
      stripAll(tokenExpression),
      action.key,
      newExpr
    );

    // At this point, we've got the new token expression with the modified argument settings
    // We'll replace the current in-line version of this token expression that exists in the SlateJS editor
    // with this modified one
    Transforms.setNodes(
      editor,
      // @ts-expect-error - TS2345 - Argument of type '{ token: string; }' is not assignable to parameter of type 'Partial<Node>'.
      {token: stringifyExpression(newTokenExpr)},
      {at: pillSlatePath, unit: 'word'}
    );

    onChange();
  };

  const showFunSettings = settingsOpenState.opened || settingsOpenState.hovered;
  const tokenExpressionHasHoles: boolean =
    Boolean(tokenExpression) && hasHoles(tokenExpression);
  return (
    <PillWrapper
      {...attributes}
      onMouseOver={handlers.onHover}
      onMouseLeave={handlers.onLeave}
      contentEditable={false}
    >
      <ExpressionBinding
        expression={tokenExpression}
        onChange={onBindingChange}
        button={(openPopover, targetRef) => (
          <span ref={targetRef}>
            <StyledSelectedBackground
              data-automation-id={`binding-pill_${tokenLabel}`}
              selected={Boolean(showAsSelected)}
            >
              <Hitarea>
                <Pill
                  needsConfig={tokenExpressionHasHoles}
                  onClick={openPopover}
                  selected={showAsSelected}
                  showOutline={selected}
                >
                  {tokenExpressionHasHoles && <HasHolesIcon />}
                  <OnelineLabel path={tokenLabel} type={tokenExprAnn.type}>
                    {tokenShortLabel}
                  </OnelineLabel>
                </Pill>
              </Hitarea>
            </StyledSelectedBackground>
          </span>
        )}
        bindUIVariant="custom"
      />

      {showFunSettings && (
        <StyledSelectedBackground
          selected={Boolean(showAsSelected)}
          settingsPosition
          isSettingsOpen={settingsOpenState.opened}
        >
          <ArgumentSettings
            fun={fun}
            dispatch={argumentsDispatch}
            label={tokenLabel}
            args={args}
            onClose={handlers.onClose}
          >
            {(openPopover) => (
              <Hitarea>
                <Settings
                  selected={showAsSelected}
                  onClick={chainFunctions(openPopover, handlers.onOpen)}
                  showOutline={showOutline}
                >
                  <SettingsIcon />
                </Settings>
              </Hitarea>
            )}
          </ArgumentSettings>
        </StyledSelectedBackground>
      )}
      {children}
    </PillWrapper>
  );
});

const getFullTokenExpression = (
  editorChildren: SlateEditorNodes,
  pillPath: Array<number>,
  expr: ContextualExpression
) => {
  const [lineNumber, nodeNumber] = pillPath;
  let tokenNumber = -1;

  // Counts the number of pills encountered on each line of the Slate editor input
  editorChildren.some((line, lineIndex) => {
    return line.children.some((node, nodeIndex) => {
      // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'SlateNode'. | TS2339 - Property 'type' does not exist on type 'SlateNode'.
      if (node.type && node.type === BINDED_PILL) {
        tokenNumber += 1;
      }
      // Exit once we've reached the line number and node number of this pill
      return lineIndex === lineNumber && nodeIndex === nodeNumber;
    });
  });

  const tokenList = listOfTokens(expr);
  const tokenExpression = tokenList[tokenNumber];

  return {tokenNumber, tokenExpression};
};
